import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { aceitoDaUrl, uuidDaUrl } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { data, textoLivre } from '../../nucleo/formato';
import { AvisoModo, useSessaoFluxo } from './AvisoModo';
import { DetalheRequisicao } from './DetalheRequisicao';
import { FormRequisicao } from './FormRequisicao';
import { COR_REQUISICAO, ESTADOS_REQUISICAO, type Requisicao } from './tipos';

/** Aba Requisições (M10.1): pedidos internos de compra, do rascunho à conversão em pedido. */
export function Requisicoes() {
  const { t } = useTranslation('compras');
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const [busca, setBusca] = useSearchParams();
  const [novo, setNovo] = useState(false);
  // Review r29 robustez (O2): `?requisicao=` que não é uuid é ignorado (a ficha não abre com erro).
  const [aberto, setAberto] = useState<string | null>(uuidDaUrl(busca.get('requisicao')) || null);
  // Link de outra tela (`?requisicao=<id>`) abre a ficha mesmo com a aba já aberta.
  useEffect(() => {
    const id = uuidDaUrl(busca.get('requisicao'));
    if (id) setAberto(id);
  }, [busca]);
  // Review r29 robustez (B2, varredura): estado que a API recusaria (`?estado=xx`) é ignorado; antes a lista caía em "Indisponível".
  const estado = aceitoDaUrl(busca.get('estado'), ESTADOS_REQUISICAO);

  const colunas: Coluna<Requisicao>[] = [
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, ordenavel: true, render: (r) => <span className="font-medium">{r.numero}</span> },
    {
      chave: 'justificativa',
      rotulo: t('campos.justificativa'),
      principal: true,
      render: (r) => (
        <span className="flex min-w-0 max-w-md flex-col">
          <span className="truncate" title={textoLivre(r.justificativa)}>{textoLivre(r.justificativa)}</span>
          <span className="truncate text-xs text-tinta-3" title={textoLivre(r.solicitante_nome)}>{textoLivre(r.solicitante_nome)}</span>
        </span>
      ),
    },
    { chave: 'itens', rotulo: t('campos.itens'), alinhar: 'direita', mono: true, render: (r) => r.itens },
    { chave: 'cotacoes', rotulo: t('campos.cotacoes'), alinhar: 'direita', mono: true, render: (r) => r.cotacoes },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), ordenavel: true, mono: true, render: (r) => data(r.created_at) },
    { chave: 'estado', rotulo: t('campos.estado'), render: (r) => <Selo cor={COR_REQUISICAO[r.estado]}>{t(`estadoRequisicao.${r.estado}`)}</Selo> },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AvisoModo />
      <AcaoPrimaria>
        {s.pode('compras.excluir') && <BotaoLixeira entidades={['compras.requisicao']} />}
        {s.pode('compras.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.novaRequisicao')}
          </Botao>
        )}
      </AcaoPrimaria>

      <Tabela<Requisicao>
        tela="compras.requisicoes"
        caminho="/compras/requisicoes"
        params={{ estado: estado || undefined }}
        filtros={{ estado }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        entidade="compras.requisicao"
        aoClicar={(r) => setAberto(r.id)}
        filtrosExtra={
          <Selecao
            aria-label={t('campos.estado')}
            vazio={t('filtros.todosEstados')}
            opcoes={ESTADOS_REQUISICAO.map((e) => ({ valor: e, rotulo: t(`estadoRequisicao.${e}`) }))}
            value={estado}
            onChange={(e) =>
              setBusca(
                (atual) => {
                  const n = new URLSearchParams(atual);
                  if (e.target.value) n.set('estado', e.target.value);
                  else n.delete('estado');
                  return n;
                },
                { replace: true },
              )
            }
          />
        }
        vazio={<Vazio texto={t('vazio.requisicoes')} />}
      />

      <FormRequisicao aberto={novo} aoFechar={() => setNovo(false)} aoSalvar={(id) => setAberto(id)} />
      <DetalheRequisicao
        id={aberto}
        aoFechar={() => {
          setAberto(null);
          if (busca.get('requisicao')) {
            setBusca(
              (atual) => {
                const n = new URLSearchParams(atual);
                n.delete('requisicao');
                return n;
              },
              { replace: true },
            );
          }
        }}
      />
    </div>
  );
}
